import { useQuery } from '@apollo/client/react';
import {
  buttonVariants,
  DimeButton,
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardHeader,
  DimeCardTitle,
  DimeDataTable,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { ListErrorState } from '../../../components/ListErrorState.js';
import { PermissionGuard } from '../../../lib/permission-guard.js';
import { usePermissions } from '../../../modules/iam/permissions-context.js';
import { CreateLeafLinkCompanyDialog } from '../../../modules/leaflink-companies/create-leaflink-company-dialog.js';
import { makeLeafLinkCompanyColumns } from '../../../modules/leaflink-companies/leaflink-company-list-columns.js';
import { ManageLeafLinkCompanyBrandsDialog } from '../../../modules/leaflink-companies/manage-leaflink-company-brands-dialog.js';
import {
  GET_LEAFLINK_COMPANIES,
  type GetLeafLinkCompaniesQueryResult,
  type GetLeafLinkCompaniesQueryVariables,
} from '../../../modules/leaflink-companies/operations.js';
import { useLeafLinkCompaniesQueryVariables } from '../../../modules/leaflink-companies/use-leaflink-companies-query-variables.js';

export const Route = createFileRoute('/integrations/leaflink/')({
  component: LeafLinkCompaniesPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function LeafLinkCompaniesPage() {
  const { permissions, hasActiveBrandMembership } = usePermissions();
  const canEdit = permissions.includes('leafLinkIntegration:edit');
  const canManageBrands = canEdit && hasActiveBrandMembership;
  const variables = useLeafLinkCompaniesQueryVariables();
  const { canViewHeldOrders } = variables;
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [managingBrandsCompanyId, setManagingBrandsCompanyId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<
    GetLeafLinkCompaniesQueryResult,
    GetLeafLinkCompaniesQueryVariables
  >(GET_LEAFLINK_COMPANIES, {
    variables,
  });

  const companies = data?.leafLinkCompanies.edges.map((edge) => edge.node) ?? [];
  const managingBrandsCompany =
    companies.find((company) => company.id === managingBrandsCompanyId) ?? null;

  const columns = useMemo(
    () =>
      makeLeafLinkCompanyColumns({
        canEdit,
        canManageBrands,
        canViewHeldOrders,
        onManageBrands: setManagingBrandsCompanyId,
        onChanged: () => void refetch(),
      }),
    [canEdit, canManageBrands, canViewHeldOrders, refetch],
  );

  const newCompanyAction = (
    <PermissionGuard
      required={['leafLinkIntegration:edit']}
      pass={
        <DimeButton type="button" onClick={() => setCreateDialogOpen(true)}>
          + New LeafLink Company
        </DimeButton>
      }
      fail={
        <span className={buttonVariants({})} role="button" aria-disabled="true">
          + New LeafLink Company
        </span>
      }
    />
  );

  return (
    <DimeCard borderless className="gap-0 py-0">
      <DimeCardHeader className="px-0 mb-4">
        <DimeCardTitle className="text-base flex items-center gap-2">
          LeafLink Companies
        </DimeCardTitle>
        <DimeCardDescription className="text-sm">
          Manage LeafLink credentials and their assigned brands.
        </DimeCardDescription>
        <DimeCardAction>{newCompanyAction}</DimeCardAction>
      </DimeCardHeader>
      {error ? (
        <ListErrorState
          title="Could not load LeafLink companies"
          message={error.message}
          onRetry={() => void refetch()}
        />
      ) : (
        <DimeDataTable
          columns={columns}
          data={companies}
          isLoading={loading}
          emptyMessage="No LeafLink companies yet."
        />
      )}
      <CreateLeafLinkCompanyDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
      <ManageLeafLinkCompanyBrandsDialog
        open={managingBrandsCompanyId !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setManagingBrandsCompanyId(null);
        }}
        company={managingBrandsCompany}
      />
    </DimeCard>
  );
}
